/**
 * Local notifications for the realtime `notification` event (contracts NotificationEvent): the server already chose
 * who gets one (the push rules); packages/core logic/notify decides whether to show it here (not my own message,
 * not the conversation on screen in a focused window). Also the app-icon badge (unread mentions + DMs) and opening
 * the conversation when a notification is clicked.
 *
 * - Desktop app: `notify_show` (UNUserNotificationCenter on macOS, the Tauri plugin elsewhere; the route and this
 *   window's label let Rust bring it forward and emit `bundle:notification-click`; apps/desktop/src-tauri/src/
 *   notifications.rs) and the Dock badge. Notifications are the app's own there, on once the OS allows them
 *   (desktopPush, a `local` PushPort, reads the OS permission).
 * - Browser: FCM web push already notifies a hidden tab when this browser has push on, so the local path only runs
 *   where web push can't (unsupported or unconfigured build) and the permission was granted — never both for one
 *   message. Shown through the service worker (sw.ts handles the click) with the conversation as the tag. A huddle
 *   ring is the exception: ringAlert.ts shows it with Join / Decline and closes it when the ring ends.
 * - Desktop deep links (`bundle://acme/c/general`) and the macOS menu's Close Tab (⌘W) are routed to the same shell
 *   (connectDesktop: the window's listeners, then `shell_ready`; apps/desktop/INTEGRATION.md §4).
 * - Sound (notificationSound.ts, core `soundDecision`): rings for the same events in any window that isn't showing
 *   that conversation, whether or not OS notifications are on here. It is the only sound: desktop notifications are
 *   sent without `sound` (the OS stays silent) and browser ones shown here are `silent`.
 */
import type { NotificationEvent } from "@bundle/contracts";
import { Huddles, Notify, Shell, Workspace, type NotificationsViewModel, type PushPermission, type PushPort, type ShellViewModel, type WorkspaceViewModel } from "@bundle/core";
import { format, type Locale } from "@bundle/i18n";
import { isDesktop, notificationsOs, openInNewWindow, type DeepLink, type DesktopMenuAction } from "./desktop.ts";
import { notificationSound } from "./notificationSound.ts";

// ---- Desktop permission (PushPort, local) ----

/** The OS's last answer, so the first paint matches it before `notify_permission` returns. */
const OS_PERMISSION_KEY = "bundle.desktopNotifications.os.v1";

function storedPermission(): PushPermission {
  try {
    const v = localStorage.getItem(OS_PERMISSION_KEY);
    return v === "denied" || v === "default" ? v : "granted";
  } catch {
    return "granted";
  }
}

/**
 * The desktop app's PushPort: no FCM, nothing registered with the server (notifications come over the WebSocket).
 * The permission is the OS's (apps/desktop/src-tauri/src/notifications.rs): macOS asks once at launch and the person
 * can turn Bundle off in System Settings, Windows has its per-app switch. Re-read whenever a window gets focus (back
 * from System Settings); `onChange` then refreshes the view model, so the "turned off" warning comes and goes.
 */
export function desktopPush(onGranted: () => void, onChange: () => void): PushPort {
  let permission = storedPermission();
  const read = async (ask: boolean): Promise<PushPermission> => {
    const { invoke } = await import("@tauri-apps/api/core");
    const answer = await invoke<PushPermission>(ask ? "notify_request_permission" : "notify_permission");
    if (answer !== permission) {
      permission = answer;
      try {
        localStorage.setItem(OS_PERMISSION_KEY, answer);
      } catch {
        // Private storage: read again next launch.
      }
      onChange();
    }
    return answer;
  };
  const reread = () => void read(false).catch((err: unknown) => console.warn("notification permission", err));
  reread();
  window.addEventListener("focus", reread);
  return {
    local: true,
    availability: () => "available",
    permission: () => permission,
    async requestPermission() {
      const answer = await read(true);
      if (answer === "granted") onGranted();
      return answer;
    },
    openSettings: () => void import("@tauri-apps/api/core").then(({ invoke }) => invoke("notify_open_settings")).catch((err: unknown) => console.warn("notification settings", err)),
    ...(notificationsOs() === undefined ? {} : { os: notificationsOs() }),
    test: (title, body) =>
      void import("@tauri-apps/api/core")
        .then(({ invoke }) => invoke("notify_show", { notification: { id: 1, title, body, group: "test" } }))
        .catch((err: unknown) => console.warn("test notification", err)),
    token: () => Promise.reject(new Error("The desktop app has no push token")),
    deleteToken: () => Promise.resolve(),
  };
}

// ---- Showing ----

type Shown = { title: string; body: string; tag: string; group: string; url: string };

/** A positive 31-bit id per tag: the plugin replaces a notification shown with the same id. */
function idFor(tag: string): number {
  let h = 0;
  for (let i = 0; i < tag.length; i++) h = (Math.imul(h, 31) + tag.charCodeAt(i)) | 0;
  return (h & 0x7fffffff) || 1;
}

async function showDesktop(n: Shown): Promise<void> {
  const { invoke } = await import("@tauri-apps/api/core");
  // Silent: notificationSound.ts plays the person's pick in the webview. Rust adds this window's label for the click.
  await invoke("notify_show", { notification: { id: idFor(n.tag), title: n.title, body: n.body, group: n.group, path: n.url } });
}

async function showBrowser(n: Shown): Promise<void> {
  const registration = "serviceWorker" in navigator ? await navigator.serviceWorker.getRegistration("/") : undefined;
  // Silent: notificationSound.ts already rang for it.
  const options: NotificationOptions = { body: n.body, tag: n.tag, icon: "/icon-192.png", data: { bundleUrl: n.url }, silent: true };
  if (registration !== undefined) {
    await registration.showNotification(n.title, options);
    return;
  }
  // Dev servers have no service worker: a page notification, clicked here.
  const shown = new Notification(n.title, options);
  shown.addEventListener("click", () => {
    window.focus();
    window.dispatchEvent(new CustomEvent("bundle:open-url", { detail: n.url }));
    shown.close();
  });
}

/**
 * The desktop call window could not show (ringWindow.ts): the ring as a notification instead, the backup the design
 * keeps for it. A click opens the huddle.
 */
export function showDesktopRingBanner(ring: { title: string; body: string; url: string; huddleId: string; channelId: string }): void {
  void showDesktop({ title: ring.title, body: ring.body, tag: `ring:${ring.huddleId}`, group: ring.channelId, url: ring.url }).catch(() => undefined);
}

async function setDesktopBadge(count: number): Promise<void> {
  const { getCurrentWindow } = await import("@tauri-apps/api/window");
  await getCurrentWindow().setBadgeCount(count > 0 ? count : undefined);
}

function setBrowserBadge(count: number): void {
  const nav = navigator as Navigator & { setAppBadge?: (n?: number) => Promise<void>; clearAppBadge?: () => Promise<void> };
  if (count > 0) void nav.setAppBadge?.(count).catch(() => undefined);
  else void nav.clearAppBadge?.().catch(() => undefined);
}

// ---- The notifier ----

export type NotifierDeps = {
  notifications: NotificationsViewModel;
  /** Browser only: web push (FCM) covers this browser, so the local path stays quiet. */
  webPushAvailable: () => boolean;
  locale: () => Locale;
};

/** What the workspace on screen provides (WorkspaceRoot attaches it). */
export type NotifierSession = {
  meId: string;
  shell: ShellViewModel;
  workspace: WorkspaceViewModel;
  /** Do not disturb is on right now (core DndViewModel): no sound, no banner. */
  paused?: () => boolean;
  /** Huddle rings have their own browser notification here, with Join / Decline (RingAlerts.tsx, ringAlert.ts). */
  rings?: boolean;
};

export class Notifier {
  /**
   * Attached shells, newest last; the newest is the one on screen. A stack rather than one slot so an overlapping
   * detach (a remount, a render that never mounted and is disposed later) falls back to the shell still attached
   * instead of leaving none — which would silently drop every notification until the next attach.
   */
  private readonly sessions: NotifierSession[] = [];
  private badge = -1;
  /** Desktop: the window's event listeners + `shell_ready`, set up on the first attach. */
  private desktop: Promise<void> | undefined;
  /** Desktop links that arrived while no workspace shell was on screen (signed out): opened on the next attach. */
  private readonly pendingRoutes: string[] = [];

  constructor(private readonly deps: NotifierDeps) {}

  /** The workspace shell is on screen: notifications open there, its unread mentions drive the badge. */
  private get session(): NotifierSession | undefined {
    return this.sessions.at(-1);
  }

  attach(session: NotifierSession): () => void {
    this.sessions.push(session);
    const offs: (() => void)[] = [];
    const sync = () => this.syncBadge();
    offs.push(session.workspace.subscribe(sync));
    sync();
    const onOpen = (e: Event) => {
      const url = (e as CustomEvent<string>).detail;
      // Cancelled = taken here (desktopLinks.ts opens a new window when no shell took it).
      if (this.session !== session) return;
      if (this.openHere(session, url)) e.preventDefault();
      else if (!e.cancelable) this.openElsewhere(url);
    };
    window.addEventListener("bundle:open-url", onOpen);
    offs.push(() => window.removeEventListener("bundle:open-url", onOpen));
    if (isDesktop()) {
      // Window-scoped listeners for the page's lifetime; `shell_ready` once they exist (INTEGRATION.md §4).
      this.desktop ??= this.connectDesktop().catch((err: unknown) => console.warn("desktop shell bridge failed", err));
      for (const path of this.pendingRoutes.splice(0)) this.route(path);
    } else if ("serviceWorker" in navigator) {
      const onMessage = (e: MessageEvent) => {
        const data = e.data as { type?: unknown; url?: unknown } | null;
        if (data?.type === "bundle:open-url" && typeof data.url === "string" && this.session === session) this.route(data.url);
      };
      navigator.serviceWorker.addEventListener("message", onMessage);
      offs.push(() => navigator.serviceWorker.removeEventListener("message", onMessage));
    }
    return () => {
      for (const off of offs.splice(0)) off();
      const i = this.sessions.lastIndexOf(session);
      if (i < 0) return;
      const wasCurrent = i === this.sessions.length - 1;
      this.sessions.splice(i, 1);
      if (!wasCurrent) return;
      // Back to the shell still attached, or (signed out, left the workspace) no badge until the next one.
      if (this.session === undefined) this.setBadge(0);
      else this.syncBadge();
    };
  }

  /**
   * Opens an in-app URL in the shell on screen: a plain open, so a view that is already open gets its tab or pane
   * focused (ShellViewModel.open). False when the URL is another workspace's (or not a workspace URL).
   */
  private openHere(session: NotifierSession, url: string): boolean {
    const ws = Shell.parseUrl(url)?.ws;
    if (ws === undefined || ws !== Shell.parseUrl(session.shell.state.homeUrl)?.ws) return false;
    session.shell.open(url, "current");
    return true;
  }

  /** Another workspace's URL: its own Bundle window on desktop, a navigation in the browser. */
  private openElsewhere(url: string): void {
    if (isDesktop()) void openInNewWindow(url).catch((err: unknown) => console.warn("failed to open", url, err));
    else location.assign(url);
  }

  /** A deep link / notification click: this window's shell, another window, or later when no shell is on screen. */
  private route(path: string): void {
    const s = this.session;
    if (s === undefined) this.pendingRoutes.push(path);
    else if (!this.openHere(s, path)) this.openElsewhere(path);
  }

  /**
   * Desktop events for this window (INTEGRATION.md §4): notification clicks, `bundle://` deep links and the macOS
   * menu's Close Tab (⌘W). Then `shell_ready`, which turns ⌘W into `bundle:menu` and hands over links that arrived
   * before this window was listening (e.g. the one that launched the app).
   */
  private async connectDesktop(): Promise<void> {
    const [{ invoke }, { getCurrentWebviewWindow }] = await Promise.all([import("@tauri-apps/api/core"), import("@tauri-apps/api/webviewWindow")]);
    const win = getCurrentWebviewWindow();
    const onPath = (e: { payload: { path: string | null } }) => {
      if (e.payload.path) this.route(e.payload.path);
    };
    await Promise.all([
      win.listen<{ path: string | null }>("bundle:notification-click", onPath),
      win.listen<DeepLink>("bundle:deep-link", onPath),
      win.listen<DesktopMenuAction>("bundle:menu", (e) => {
        if (e.payload.action === "close-tab") this.closeTab(() => void win.close());
      }),
    ]);
    const pending = await invoke<DeepLink[]>("shell_ready");
    for (const link of pending) if (link.path) this.route(link.path);
  }

  /** ⌘W: closes the active tab; the last one closes the window (the main window hides). */
  private closeTab(closeWindow: () => void): void {
    const shell = this.session?.shell;
    if (shell === undefined || shell.state.tabs.length <= 1) closeWindow();
    else shell.closeTab();
  }

  /** WorkspaceViewModel `onNotification`. */
  notify(event: NotificationEvent): void {
    const s = this.session;
    if (s === undefined) return;
    const focused = document.visibilityState === "visible" && document.hasFocus();
    const open = this.openConversations(s);
    const paused = s.paused?.() === true;
    // A huddle ring rings with the ringtone instead (IncomingHuddle.tsx); "Ana declined" is mine, so silent anyway.
    if (event.about !== "huddle") notificationSound.ring(event, { meId: s.meId, focused, open, paused });
    // Desktop: a ring shows in the call window (and bounces the Dock), not as a banner (ringWindow.ts).
    else if (isDesktop() && Huddles.incomingRing(event, s.meId, undefined) !== undefined) return;
    if (!isDesktop() && s.rings === true && Huddles.incomingRing(event, s.meId, undefined) !== undefined) return;
    const decision = Notify.notifyDecision(event, { meId: s.meId, enabled: this.enabled(), focused, open, paused });
    if (!decision.show) return;
    const shown: Shown = { title: event.title, body: event.body, tag: decision.tag, group: decision.group, url: event.url };
    void (isDesktop() ? showDesktop(shown) : showBrowser(shown)).catch((err: unknown) => console.warn("notification failed", err));
  }

  /** "Notifications are on" right after turning them on (desktop), which is also when macOS asks for permission. */
  confirm(): void {
    if (!isDesktop()) return;
    const locale = this.deps.locale();
    const title = format(locale, "notifications.enabled.title");
    void showDesktop({ title, body: format(locale, "notifications.enabled.body"), tag: "bundle:enabled", group: "bundle", url: "" }).catch(() => undefined);
  }

  private enabled(): boolean {
    if (isDesktop()) return this.deps.notifications.state.status === "on";
    // Browser: only where FCM can't do it, and only with the permission already granted.
    return !this.deps.webPushAvailable() && "Notification" in window && Notification.permission === "granted";
  }

  private openConversations(s: NotifierSession): Notify.OpenConversation[] {
    const state = s.shell.state;
    const cache = s.workspace.state.cache;
    const dms = cache === undefined ? {} : Notify.dmChannelsByPerson(cache.channels, cache.meId);
    const out: Notify.OpenConversation[] = [];
    for (const pane of Shell.panes(state.layout)) {
      const tab = Shell.findTab(state, pane.tabId);
      const open = tab === undefined ? undefined : Notify.openConversation(tab.url, dms);
      if (open !== undefined) out.push(open);
    }
    return out;
  }

  private syncBadge(): void {
    const cache = this.session?.workspace.state.cache;
    if (cache === undefined) return;
    this.setBadge(Workspace.totalBadge(cache));
  }

  private setBadge(count: number): void {
    if (count === this.badge) return;
    this.badge = count;
    if (isDesktop()) void setDesktopBadge(count).catch(() => undefined);
    else setBrowserBadge(count);
  }
}
